import { useEffect } from 'react';
import Boundary from '@app/Boundary';
import RoutesRoot from '@routes/index';
import { ThemeProvider } from 'styled-components';
import { getTheme, buildFinalAntdConfig } from '@theme/theme';
import MessageProvider from '@context/MessageProvider';
import AbilityProvider from '@/ability/AbilityProvider';
import { initializeHttpInterceptors } from '@utils/http';
import ViteHMRHandler from '@/app/configs/ViteHMRHandler';
import NotificationProvider from '@context/NotificationProvider';
import PreloadErrorOverlay from '@/app/configs/PreloadErrorOverlay';
import AppUpdateWatcher from '@/app/configs/AppUpdateWatcher';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntApp } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useFirebaseAuthListener } from '@hooks/useFirebaseAuthListener';
import { useAuthStore } from '@stores/auth.store';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1
        }
    }
});

const showTanStackQueryDevtools =
    typeof window !== 'undefined' &&
    (import.meta.env.DEV ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

const App = () => {
    useFirebaseAuthListener();
    const primaryColor = useAuthStore((s) => s.user?.preferences?.primaryColor) || '#F04A4A';
    const currentTheme = getTheme('light', { primaryColor });
    const finalAntdConfig = buildFinalAntdConfig('light', { primaryColor });

    useEffect(() => {
        initializeHttpInterceptors();
    }, []);

    useEffect(() => {
        document.documentElement.style.colorScheme = 'light';
    }, []);

    return (
        <ConfigProvider theme={finalAntdConfig}>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={currentTheme}>
                    <AntApp>
                        <MessageProvider>
                            <NotificationProvider>
                                <PreloadErrorOverlay />
                                <ViteHMRHandler />
                                <AppUpdateWatcher />
                                <AbilityProvider>
                                    <Boundary>
                                        <RoutesRoot />
                                    </Boundary>
                                </AbilityProvider>
                            </NotificationProvider>
                        </MessageProvider>
                    </AntApp>
                </ThemeProvider>
                {showTanStackQueryDevtools ? (
                    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
                ) : null}
            </QueryClientProvider>
        </ConfigProvider>
    );
};

export default App;
